import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { map, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage.service';
import { getBrowser, getCountryHeaderValue, getDeviceType, isBraveBrowser, isDeviceId } from '../utils/device-info';
import { readDeviceIdCookie, writeDeviceIdCookie } from '../utils/device-id-cookie';
import { devLog } from '../utils/dev-log';

/**
 * Deliberately **not** in `WagmiConfigService.disconnectAndClearSession()`: the id names the
 * browser, not the session, so it must survive logout and session expiry (backend contract).
 */
const DEVICE_ID_KEY = 'device_id';

/** The register call never blocks anything, so this only bounds how long it may stay pending. */
const REGISTER_TIMEOUT_MS = 8000;

/**
 * Where the user's profile is stored after login (see WalletAuthOrchestratorService / AuthService).
 * `x-country` is read from `user.country` on it, so the header follows the profile the user chose.
 */
const USER_DETAILS_KEY = 'userDetails';

interface DeviceRegisterResponse {
  data?: { device_id?: string };
}

/**
 * Identifies this browser to the backend's request log.
 *
 * At app start `start()` sends `POST api/v1/user/device/register` with the stored id (or null);
 * the backend returns the same id for a known device or issues a new one. From then on
 * `DeviceInfoInterceptor` sends `x-device-id`, `x-device-type` and `x-browser` on every API
 * request, which is also how the backend links the device to the user at login. `x-browser` is
 * the user-agent label, except that Brave (which reports Chrome's user agent) is relabelled
 * `Brave <n>` once the async `navigator.brave.isBrave()` check confirms it. After login it
 * also sends `x-country` from the user's profile; a request made before login carries none.
 *
 * Monitoring only: nothing waits on this service, it shows nothing to the user, and every
 * failure leaves the app exactly as it would be without it. Off unless
 * `environment.DEVICE_TRACKING_ENABLED` is true.
 */
@Injectable({
  providedIn: 'root'
})
export class DeviceTrackingService {
  /**
   * Built on HttpBackend so the register call skips every interceptor: a 401 on it must never
   * reach AuthInterceptor's session-expiry teardown, and it needs no Authorization header.
   */
  private readonly http: HttpClient;
  private deviceId: string | null = null;
  private detailHeaders: Record<string, string> | null = null;
  private braveDetected = false;
  private started = false;

  constructor(
    httpBackend: HttpBackend,
    private readonly localStorageService: LocalStorageService
  ) {
    this.http = new HttpClient(httpBackend);
  }

  /**
   * Registers this browser once per app load and keeps the id the backend returns.
   * Fire-and-forget: until it answers, requests carry the stored id, or none on a first visit.
   */
  public start(): void {
    if (!environment.DEVICE_TRACKING_ENABLED || this.started) return;
    this.started = true;

    this.detectBrave();

    this.http
      .post<DeviceRegisterResponse>(
        `${environment.API_BASE_URL}api/v1/user/device/register`,
        { device_id: this.getDeviceId() },
        { headers: this.getHeaders() }
      )
      .pipe(
        timeout(REGISTER_TIMEOUT_MS),
        map(response => {
          const deviceId = response?.data?.device_id;
          if (!isDeviceId(deviceId)) {
            throw new Error('Device register response carried no device_id');
          }
          return deviceId;
        })
      )
      .subscribe({
        next: deviceId => {
          this.saveDeviceId(deviceId);
          devLog('Device registered for request logging');
        },
        error: error => console.error('Device register failed:', error)
      });
  }

  /**
   * The device headers for one API request. Synchronous, so the interceptor never waits.
   * @returns `x-device-type`, `x-browser`, `x-device-id` once an id is known, and `x-country`
   *          once the user has logged in and their profile country is known
   */
  public getHeaders(): Record<string, string> {
    const headers = { ...this.getDetailHeaders() };
    const deviceId = this.getDeviceId();
    if (deviceId) headers['x-device-id'] = deviceId;
    const country = this.readStoredCountry();
    if (country) headers['x-country'] = country;
    return headers;
  }

  /**
   * The user's profile country, read fresh each call so a profile change is reflected without a
   * reload. Only known after login; before that, and for a profile with no country, there is none.
   */
  private readStoredCountry(): string | null {
    try {
      const details = this.localStorageService.getDataByKey(USER_DETAILS_KEY);
      return getCountryHeaderValue(details?.user?.country);
    } catch {
      // localStorage unavailable or the stored value is not shaped as expected: send no country.
      return null;
    }
  }

  /**
   * Brave hides itself in the user agent, so once (per app load) we ask the async
   * `navigator.brave.isBrave()` API and, if it is Brave, drop the cached detail headers so the
   * next request rebuilds `x-browser` as `Brave <n>` instead of `Chrome <n>`. Fire-and-forget and
   * fail-closed: a non-Brave browser or a thrown check simply leaves the UA-derived label in place,
   * so the register call (sent before this resolves) keeps `Chrome <n>` and nothing waits on it.
   */
  private detectBrave(): void {
    isBraveBrowser()
      .then(isBrave => {
        if (!isBrave) return;
        this.braveDetected = true;
        this.detailHeaders = null;
      })
      .catch(() => {
        // Brave check unavailable or failed: keep the user-agent label.
      });
  }

  /** Device type and browser do not change within a page load, so they are read once. */
  private getDetailHeaders(): Record<string, string> {
    if (!this.detailHeaders) {
      const headers: Record<string, string> = { 'x-device-type': getDeviceType() };
      let browser = getBrowser();
      // Brave reports Chrome's user agent; when the async check confirmed Brave, relabel it.
      if (browser && this.braveDetected) browser = browser.replace(/^Chrome/, 'Brave');
      if (browser) headers['x-browser'] = browser;
      this.detailHeaders = headers;
    }
    return this.detailHeaders;
  }

  private getDeviceId(): string | null {
    if (!this.deviceId) this.deviceId = this.readStoredDeviceId();
    return this.deviceId;
  }

  /**
   * localStorage first; the cookie backup covers the error paths that call
   * `localStorage.clear()`, and puts the id back into localStorage when it finds it there.
   */
  private readStoredDeviceId(): string | null {
    try {
      const stored = this.localStorageService.getDataByKey(DEVICE_ID_KEY);
      if (isDeviceId(stored)) return stored;
    } catch {
      // localStorage unavailable (blocked by the browser): fall back to the cookie.
    }
    const backup = readDeviceIdCookie();
    if (backup) this.storeLocally(backup);
    return backup;
  }

  private saveDeviceId(deviceId: string): void {
    this.deviceId = deviceId;
    this.storeLocally(deviceId);
    writeDeviceIdCookie(deviceId);
  }

  private storeLocally(deviceId: string): void {
    try {
      this.localStorageService.storeData(DEVICE_ID_KEY, deviceId);
    } catch {
      // localStorage unavailable: the in-memory id and the cookie still hold it.
    }
  }
}
